import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { formatCount, formatUsd } from '@kioskgaming/ui';
import type { DashboardV2Period, ShopDashboardV2RoadblocksData } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  data: ShopDashboardV2RoadblocksData | null;
  isLoading: boolean;
};

export default function RoadblocksTab({ data, isLoading }: Props) {
  if (isLoading) {
    return <p className="py-12 text-center text-sm text-gray-500">Loading roadblocks…</p>;
  }

  const blocks = data?.roadblocks ?? [];

  if (blocks.length === 0) {
    return (
      <div className="rounded-2xl border border-blue-200 bg-blue-50/40 px-6 py-12 text-center">
        <p className="text-sm font-medium text-blue-800">No active roadblocks</p>
        <p className="mt-1 text-xs text-blue-700">Your Shop has no flagged blockers right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {blocks.map((block) => (
        <Link
          key={block.id}
          to={block.link}
          className="flex items-center justify-between gap-4 rounded-2xl border border-blue-200 bg-white px-4 py-4 transition hover:border-blue-300 hover:bg-blue-50/30"
        >
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="font-medium text-gray-900">{block.label}</p>
              <p className="text-xs text-gray-500">
                {formatCount(block.count)} item{block.count === 1 ? '' : 's'}
                {block.amountUsd > 0 ? ` · ${formatUsd(block.amountUsd)}` : ''}
              </p>
            </div>
          </div>
          <span className="shrink-0 text-xs font-semibold text-blue-700">View →</span>
        </Link>
      ))}
    </div>
  );
}
